// Connect Jira, one step at a time (#599), in the order #581 Q1 settled.
//
// The flow asks for its settings through the field components (`jiraFields.tsx`), so its
// words cannot drift from the ones #578 and #571 pinned. What is the flow's own is the
// order, the summary, and the state: a pure reducer, so that going back never costs a
// person what they typed and a second press of Connect never makes a second connection —
// both claimed in `JiraFlow.test.ts`.
//
// Saving is the existing create and nothing else; why it carries no check is #581 Q3.
// The optional test work item (#614) is its own call after that save has returned, off
// unless ticked (#581 Q3b), and lives in `connectThenTest` so its order is claimed
// without a DOM.
//
// The choose step reads Jira's own lists (#600, #581 Q2) with the credential the sign-in
// step was given, so the token stays in this state until the save — and is dropped by
// every way out: saved, cancelled, and back at the first step. One page of each list is
// read; where Jira has more, the flow says so and takes the value typed instead.
//
// **Editing a connection is this flow too** (#817), opened at the choose step with the
// connection's settings chosen. The browser holds no token for a saved connection, so
// its lists are read by the connection's id and the server opens its own sealed pair;
// the sign-in step is reached only by choosing to replace the token, and then the lists
// are read with the one typed. What a save sends is the edit's (`jiraEdit`, #580): only
// what changed, and never a token that was not replaced. `jiraEdit` is read from
// `Integrations.tsx`, which renders this flow, so the two import each other as
// `SlackFlow.tsx` and it do: each uses the other only inside functions.

import { useEffect, useReducer, useRef, type FormEvent, type ReactNode } from "react";

import { ApiError, messageOf } from "../api/http";
import {
  jiraTargetOf,
  SEVERITY_LABEL,
  slackChannelShown,
  targetOf,
  type IntegrationRow,
  type JiraConfig,
  type JiraPatch,
  type JiraRow,
  type NewIntegration,
  type NewJira,
  type SlackConfig,
} from "../api/integrations";
import type { JiraAsking, JiraLister, JiraSpace, JiraWorkType, Listed, ListedSpaces, ListedWorkTypes } from "../api/jiraLists";
import { Notice } from "./chrome";
import { failedCheckInWords, healthInWords } from "./connectionHealth";
import { jiraEdit, type Edit } from "./Integrations";
import {
  JiraChoice,
  JiraCredentialFields,
  JiraDestinationFields,
  JiraLabelsField,
  JiraSiteField,
  JiraSpaceKeyField,
  JiraWorkTypeField,
  WHERE_A_TOKEN_IS_MADE,
  jiraIntegration,
  settledSite,
  type JiraFields,
} from "./jiraFields";
import { StepFlow, stepAfter, stepBefore, type FlowStep } from "./StepFlow";

export type JiraStep = "needs" | "sign-in" | "where" | "confirm" | "done";

export const JIRA_STEPS: ReadonlyArray<FlowStep<JiraStep>> = [
  { id: "needs", label: "What you’ll need" },
  { id: "sign-in", label: "Sign in" },
  { id: "where", label: "Choose where to send" },
  { id: "confirm", label: "Confirm" },
  { id: "done", label: "Done" },
];

/** Editing has nothing to prepare: the connection is already made (#817). */
export const JIRA_EDIT_STEPS: ReadonlyArray<FlowStep<JiraStep>> = JIRA_STEPS.filter((step) => step.id !== "needs");

export type { JiraLister };

/** One of Jira's lists: not asked for, being read, refused (and why), or in hand. */
export type Listing<T> = "idle" | "loading" | { failed: string } | { items: T[]; total: number | null; more: boolean };

export type Listable = "spaces" | "work-types";

export interface JiraFlowState {
  step: JiraStep;
  fields: JiraFields;
  spaces: Listing<JiraSpace>;
  workTypes: Listing<JiraWorkType>;
  /** The request each list is waiting on: an answer to any other is of a credential or a space since changed. */
  asked: { spaces: number; workTypes: number };
  /** Typed by hand instead of chosen, where Jira has more than its one page holds. */
  typing: { space: boolean; workType: boolean };
  /** The space whose work types are wanted: one chosen, or a typed key once its field is left — never a key half typed. */
  listFor: string;
  siteError: string | null;
  dropped: string | null;
  saving: boolean;
  failure: string | null;
  /** Send a test work item once the connection is saved. Off unless a person ticks it. */
  sendTest: boolean;
  /** What became of that test work item, once the save has landed. */
  test: TestAsked;
  /**
   * The active connection the server named when it refused this one (#653): the workspace
   * already sends to this site and space. Set, the next Connect says the second is meant.
   */
  sameSpace: SameSpace | null;
  /** The saved connection this flow edits (#817), or null when it connects a new one. */
  editing: { id: string; config: JiraConfig } | null;
  /** Editing, and the person chose to replace the token: the lists are read with the one typed. */
  replacing: boolean;
  /** What an edit's save changed, for its last step. */
  changed: string[] | null;
}

/**
 * The connection a save was refused beside: what the warning names and Edit opens. A Jira
 * one (#653) or a Slack one (#762); the Slack flow and form read it from here too.
 */
export type SameSpace =
  | { id: string; kind: "jira"; config: JiraConfig }
  | {
      id: string;
      kind: "slack";
      config: SlackConfig;
      /** Refused because Slack would not name a workspace for one side, not because both are one (#762). */
      slackTeamUnknown: boolean;
    };

/**
 * What a person is told about the connection a save was refused beside (#653, #762): the
 * words of both flows, connecting or editing, in each kind's nouns.
 */
export function sameSpaceInWords(existing: SameSpace): string {
  if (existing.kind === "slack") {
    const { channel, severity_floor: floor } = existing.config;
    const through =
      `This workspace already posts to ${slackChannelShown(channel)}, through a Slack connection ` +
      `that posts conclusions at ${SEVERITY_LABEL[floor]} and above. `;
    return existing.slackTeamUnknown
      ? through +
          "Slack would not say which Slack workspace one of the two connects to, so it may be this same channel. " +
          "If it is, every message is posted once per connection, and a second one posts each message twice."
      : through + "Every message is posted once per connection, so a second one posts each message twice.";
  }
  const { project_key: space, base_url: site } = existing.config;
  return (
    `This workspace already sends work items to ${space} on ${site.replace(/^https?:\/\//, "").replace(/\/$/, "")}, ` +
    `through the Jira connection ${jiraTargetOf(existing.config)}. ` +
    "Every incident is filed once per connection, so a second one files each incident twice."
  );
}

/**
 * The connection an `already_connected` refusal names (#653, #762), or null for any other
 * failure. The server decides what the same destination is — a Jira site through the save
 * path's own normalisation, a Slack workspace by `auth.test` — and this only reads its answer.
 */
export function alreadyConnected(refusal: unknown): SameSpace | null {
  if (!(refusal instanceof ApiError) || refusal.status !== 409) return null;
  const detail = (refusal.body as { detail?: unknown } | null)?.detail as
    | { error?: unknown; existing?: { id?: unknown; kind?: unknown; config?: unknown }; slack_team_unknown?: unknown }
    | undefined;
  if (detail?.error !== "already_connected" || typeof detail.existing?.id !== "string") return null;
  const { id, kind, config } = detail.existing;
  if (kind === "jira") return { id, kind, config: config as JiraConfig };
  if (kind === "slack") return { id, kind, config: config as SlackConfig, slackTeamUnknown: detail.slack_team_unknown === true };
  return null;
}

/** Not wanted; asked for, with the result to follow under Health; or why it was not sent. */
export type TestAsked = "not-asked" | "asked" | { failed: string };

export const openingJiraFlow: JiraFlowState = {
  step: "needs",
  fields: { baseUrl: "", projectKey: "", issueType: "", labels: "", email: "", token: "" },
  spaces: "idle",
  workTypes: "idle",
  asked: { spaces: 0, workTypes: 0 },
  typing: { space: false, workType: false },
  listFor: "",
  siteError: null,
  dropped: null,
  saving: false,
  failure: null,
  sendTest: false,
  test: "not-asked",
  sameSpace: null,
  editing: null,
  replacing: false,
  changed: null,
};

/**
 * The flow an Edit opens (#817): at the choose step, the connection's settings in place, and
 * its lists to be read with its own saved credential. The work types are those of its space.
 */
export function editingJiraFlow(row: { id: string; config: JiraConfig }): JiraFlowState {
  const { config } = row;
  return {
    ...openingJiraFlow,
    step: "where",
    fields: {
      baseUrl: config.base_url,
      projectKey: config.project_key,
      issueType: config.issue_type,
      labels: config.labels.join(", "),
      email: "",
      token: "",
    },
    listFor: config.project_key,
    editing: { id: row.id, config },
  };
}

export type JiraFlowEvent =
  | { type: "typed"; field: keyof JiraFields; value: string }
  /** The person left the site field: read the address now, as the form's blur does. */
  | { type: "site-left" }
  | { type: "continue" }
  | { type: "back" }
  /** Leave the flow: nothing entered outlives it, the token least of all. */
  | { type: "cancelled" }
  | { type: "listing"; what: Listable; id: number }
  | { type: "spaces-listed"; id: number; answer: ListedSpaces }
  | { type: "work-types-listed"; id: number; key: string; answer: ListedWorkTypes }
  /** The API's own refusal, or no answer at all. */
  | { type: "listing-failed"; what: Listable; id: number; message: string }
  | { type: "retry"; what: Listable }
  /** A space chosen from the list, or its key typed and the field left. */
  | { type: "space-chosen"; key: string }
  | { type: "typing-instead"; what: "space" | "work-type"; on: boolean }
  | { type: "test-toggled"; on: boolean }
  /** Editing: sign in again, to list and save with a new token (#817). */
  | { type: "replace-token" }
  | { type: "saving" }
  | { type: "saved"; test?: TestAsked; changed?: string[] }
  | { type: "refused"; message: string }
  /** Refused because the workspace already sends to this site and space (#653). */
  | { type: "already-connected"; existing: SameSpace };

const withSite = (state: JiraFlowState): JiraFlowState => {
  const settled = settledSite(state.fields.baseUrl);
  // The field is read when it is left and again on Continue. The second reading is of
  // the site the first one kept, with nothing left to drop, and must not take back the
  // first one's report; typing a new address already has (`typed`, below).
  const reread = settled.siteError === null && settled.baseUrl === state.fields.baseUrl;
  return {
    ...state,
    siteError: settled.siteError,
    dropped: settled.dropped ?? (reread ? state.dropped : null),
    fields: { ...state.fields, baseUrl: settled.baseUrl },
  };
};

const SIGN_IN_FIELDS: ReadonlyArray<keyof JiraFields> = ["baseUrl", "email", "token"];

const listed = <T,>(answer: Listed, items: T[]): Listing<T> =>
  answer.status === "passed"
    ? { items, total: answer.total, more: answer.more }
    : { failed: failedCheckInWords("jira", { status: "failed", last_status: answer.last_status, last_detail: answer.last_detail, checked_at: "" }) };

/** Back to not asked for, if Jira refused it — so that it is asked for again; a list in hand is kept. */
const forgottenIfRefused = <T,>(listing: Listing<T>): Listing<T> => (typeof listing === "object" && "failed" in listing ? "idle" : listing);

/**
 * Editing, back from signing in again: the saved token is kept, so the site it is saved
 * with is too, and the lists are read with it again.
 */
const keepingTheSavedToken = (state: JiraFlowState, saved: JiraConfig): JiraFlowState => ({
  ...state,
  step: "where",
  replacing: false,
  failure: null,
  sameSpace: null,
  siteError: null,
  dropped: null,
  fields: { ...state.fields, baseUrl: saved.base_url, email: "", token: "" },
  spaces: "idle",
  workTypes: "idle",
});

/** The list is here or on its way: asking for it again would be a second question. */
const inHandOrComing = <T,>(listing: Listing<T>): boolean => listing === "loading" || (typeof listing === "object" && "items" in listing);

export function jiraFlowReducer(state: JiraFlowState, event: JiraFlowEvent): JiraFlowState {
  if (event.type === "cancelled") return state.saving ? state : openingJiraFlow;
  // A save in flight holds the flow still, and a finished flow has nowhere to go back
  // to: either would put a person one press from a second connection.
  if (state.step === "done") return state;
  if (state.saving && event.type !== "saved" && event.type !== "refused" && event.type !== "already-connected") return state;
  switch (event.type) {
    case "typed": {
      const fields = { ...state.fields, [event.field]: event.value };
      // Lists read with another site or another pair are not this one's.
      if (event.field === "projectKey" && state.typing.space) return { ...state, fields: { ...fields, issueType: "" }, workTypes: "idle", listFor: "" };
      const relisted: JiraFlowState = SIGN_IN_FIELDS.includes(event.field) ? { ...state, fields, spaces: "idle", workTypes: "idle" } : { ...state, fields };
      return event.field === "baseUrl" ? { ...relisted, siteError: null, dropped: null } : relisted;
    }
    case "site-left":
      return state.fields.baseUrl.trim() === "" ? state : withSite(state);
    case "continue": {
      if (state.step === "confirm") return state;
      // Enter in the typed key's field is Continue with the field never left: read that space's work types, as leaving it does.
      if (state.step === "where" && state.typing.space && state.fields.projectKey.trim() !== state.listFor) {
        return jiraFlowReducer(state, { type: "space-chosen", key: state.fields.projectKey });
      }
      if (state.step === "where" && (state.fields.projectKey.trim() === "" || state.fields.issueType.trim() === "")) return state;
      if (state.step !== "sign-in") return { ...state, step: stepAfter(JIRA_STEPS, state.step) };
      // Replacing a saved token is both halves of the pair: Jira authenticates them together.
      if (state.editing !== null && (state.fields.email.trim() === "" || state.fields.token === "")) return state;
      const read = withSite(state);
      return read.siteError === null ? { ...read, step: "where" } : read;
    }
    case "back": {
      if (state.editing !== null && state.step === "sign-in") return keepingTheSavedToken(state, state.editing.config);
      // An edit opens here: before it is only the sign-in a person chooses to redo.
      if (state.editing !== null && state.step === "where" && !state.replacing) return state;
      const step = stepBefore(JIRA_STEPS, state.step);
      // The warning is about the site and space confirmed; going back is to change them.
      const back = { ...state, step, failure: null, sameSpace: null, spaces: forgottenIfRefused(state.spaces), workTypes: forgottenIfRefused(state.workTypes) };
      // The first step asks for nothing, so nothing there needs the token held for it.
      return step === "needs" ? { ...back, fields: { ...state.fields, token: "" }, spaces: "idle", workTypes: "idle" } : back;
    }
    case "listing":
      return event.what === "spaces"
        ? { ...state, spaces: "loading", asked: { ...state.asked, spaces: event.id } }
        : { ...state, workTypes: "loading", asked: { ...state.asked, workTypes: event.id } };
    case "spaces-listed": {
      if (state.spaces !== "loading" || event.id !== state.asked.spaces) return state;
      const held = state.typing.space || event.answer.spaces.some((space) => space.key === state.fields.projectKey);
      // An edited connection's settings outlive a list that failed: they are what it holds.
      if (state.editing !== null && event.answer.status !== "passed") return { ...state, spaces: listed(event.answer, event.answer.spaces) };
      // An edited connection's space on a page this list did not read is still its space: typed, and kept.
      if (!held && state.editing !== null && event.answer.more && state.fields.projectKey !== "") {
        return { ...state, spaces: listed(event.answer, event.answer.spaces), typing: { ...state.typing, space: true } };
      }
      return {
        ...state,
        spaces: listed(event.answer, event.answer.spaces),
        ...(held ? {} : { fields: { ...state.fields, projectKey: "", issueType: "" }, workTypes: "idle" as const, listFor: "" }),
      };
    }
    case "work-types-listed": {
      if (state.workTypes !== "loading" || event.id !== state.asked.workTypes || event.key !== state.listFor) return state;
      const held = state.typing.workType || event.answer.work_types.some((type) => type.name === state.fields.issueType);
      if (state.editing !== null && event.answer.status !== "passed") return { ...state, workTypes: listed(event.answer, event.answer.work_types) };
      if (!held && state.editing !== null && event.answer.more && state.fields.issueType !== "") {
        return { ...state, workTypes: listed(event.answer, event.answer.work_types), typing: { ...state.typing, workType: true } };
      }
      return { ...state, workTypes: listed(event.answer, event.answer.work_types), fields: held ? state.fields : { ...state.fields, issueType: "" } };
    }
    case "listing-failed": {
      const mine = event.what === "spaces" ? state.spaces === "loading" && event.id === state.asked.spaces : state.workTypes === "loading" && event.id === state.asked.workTypes;
      if (!mine) return state;
      return event.what === "spaces" ? { ...state, spaces: { failed: event.message } } : { ...state, workTypes: { failed: event.message } };
    }
    case "retry":
      // Whatever the list said: a refusal, or nothing in it — which an admin may since have changed.
      if (event.what === "work-types") return state.workTypes === "loading" ? state : { ...state, workTypes: "idle" };
      return state.spaces === "loading" ? state : { ...state, spaces: "idle", workTypes: "idle" };
    case "space-chosen": {
      const key = event.key.trim();
      // The same space again, with its list in hand or on its way, is no new question.
      if (key === state.listFor && inHandOrComing(state.workTypes)) return state;
      return { ...state, fields: { ...state.fields, projectKey: key, issueType: "" }, workTypes: "idle", listFor: key, typing: { ...state.typing, workType: false } };
    }
    case "typing-instead":
      return event.what === "space"
        ? { ...state, typing: { space: event.on, workType: false }, fields: { ...state.fields, projectKey: "", issueType: "" }, workTypes: "idle", listFor: "" }
        : { ...state, typing: { ...state.typing, workType: event.on }, fields: { ...state.fields, issueType: "" } };
    case "test-toggled":
      return { ...state, sendTest: event.on };
    case "replace-token":
      if (state.editing === null || state.step !== "where") return state;
      return { ...state, step: "sign-in", replacing: true, fields: { ...state.fields, email: "", token: "" }, spaces: "idle", workTypes: "idle" };
    case "saving":
      return state.step === "confirm" ? { ...state, saving: true, failure: null } : state;
    case "already-connected":
      return { ...state, saving: false, failure: null, sameSpace: event.existing };
    case "saved":
      return {
        ...state,
        step: "done",
        saving: false,
        test: event.test ?? "not-asked",
        changed: event.changed ?? null,
        fields: { ...state.fields, token: "" },
      };
    case "refused":
      return { ...state, saving: false, failure: event.message };
  }
}

/** What the choose step's lists are asked with, or null on any other step: the sign-in step's entries, as a save would send them. */
export function listingAsked(state: JiraFlowState): JiraAsking | null {
  if (state.step !== "where") return null;
  // A saved connection's own token is the server's to open (#817): the browser never had it.
  if (state.editing !== null && !state.replacing) return { connection_id: state.editing.id };
  const { config, credential } = jiraIntegration(state.fields);
  return { base_url: config.base_url, credential };
}

/** The connection the confirm step would create, or null when this is no moment to create one. */
export function submission(state: JiraFlowState): NewJira | null {
  if (state.editing !== null || state.step !== "confirm" || state.saving) return null;
  const body = jiraIntegration(state.fields);
  return state.sameSpace === null ? body : { ...body, alongside_existing: true };
}

/**
 * What an edit's confirm step would send, and what it would say it did (#817, #580): only
 * what changed, the token only when it was replaced. Null on any other step, or not editing.
 * An edit with no change in it is returned too, so the step can say so; it is never sent.
 */
export function editSubmission(state: JiraFlowState): Edit<JiraPatch> | null {
  if (state.editing === null || state.step !== "confirm" || state.saving) return null;
  const edit = jiraEdit(state.editing.config, state.replacing ? state.fields : { ...state.fields, email: "", token: "" });
  return state.sameSpace === null ? edit : { ...edit, patch: { ...edit.patch, alongside_existing: true } };
}

/** What an edited connection's flow says of it first: what it sends to, and its health (#579, #580). */
export function EditingLine({ row }: { row: IntegrationRow }) {
  return (
    <p className="muted" data-testid={`editing-${row.id}`}>
      Editing {targetOf(row)}. {healthInWords(row)}
    </p>
  );
}

/** What an edit's last step says: what changed, in order (#580). */
export function SavedChanges({ changes }: { changes: string[] }) {
  return <Notice tone="good">Saved. {changes.map((change) => `${change}.`).join(" ")}</Notice>;
}

/** What an edit's confirm step says when nothing in it has changed. */
export const NOTHING_CHANGED = "Nothing has changed yet. Go back to change a setting, or replace the token.";

/**
 * One press of Connect: the save, and then — only when asked for, and only once the save
 * has returned a connection to send from — the one test work item.
 *
 * A refused save throws, as it always did, and nothing is sent. A test that could not be
 * asked for is an outcome and not a throw: the connection is saved either way, and a
 * person sent back to Confirm would be one press from a second one.
 */
export async function connectThenTest(
  body: NewIntegration,
  sendTest: boolean,
  onCreate: (body: NewIntegration) => Promise<IntegrationRow | void>,
  onSendTest?: (id: string) => Promise<unknown>,
): Promise<TestAsked> {
  const created = await onCreate(body);
  if (!sendTest) return "not-asked";
  if (onSendTest === undefined || created === undefined) return { failed: "this page cannot send one from here." };
  try {
    await onSendTest(created.id);
    return "asked";
  } catch (refusal) {
    return { failed: messageOf(refusal) };
  }
}

/** What the confirm step says back, in the order a person checks it in. */
export function jiraSummary(config: JiraConfig): { space: string; workType: string; site: string; labels: string | null } {
  return {
    space: config.project_key,
    workType: config.issue_type,
    site: config.base_url.replace(/^https?:\/\//, "").replace(/\/$/, ""),
    labels: config.labels.length === 0 ? null : config.labels.join(", "),
  };
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}

const siteOf = (baseUrl: string): string => baseUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");

/** How much of a list is on the page, where Jira has more than its one page holds. */
function unread(listing: { items: unknown[]; total: number | null }, whose: string, things: string, fallback: string): string {
  const held = listing.items.length;
  return listing.total === null
    ? `${whose} has more ${things} than the first ${held} listed here. ${fallback}`
    : `${whose} has ${listing.total} ${things} and this list holds the first ${held}. ${fallback}`;
}

/** The choose step, from Jira's own lists (#600): the space, then that space's work types, then the labels typed. */
function ChooseFromLists({
  state,
  dispatch,
  onType,
  onward,
  stepLabel,
  back,
  cancel,
}: {
  state: JiraFlowState;
  dispatch: (event: JiraFlowEvent) => void;
  onType: (field: keyof JiraFields, value: string) => void;
  onward: (event: FormEvent) => void;
  stepLabel: string | undefined;
  /** Back to sign in; or, editing with the saved token, the way to replace it (#817). */
  back: ReactNode;
  cancel: ReactNode;
}) {
  const { fields, spaces, workTypes } = state;
  const withSaved = state.editing !== null && !state.replacing;
  const again = (what: Listable) => (
    <button type="button" className="btn" onClick={() => dispatch({ type: "retry", what })}>
      Try again
    </button>
  );
  if (spaces === "idle" || spaces === "loading") {
    return (
      <>
        <p role="status">Reading the spaces this account can see…</p>
        <Actions>
          {back}
          {cancel}
        </Actions>
      </>
    );
  }
  if ("failed" in spaces) {
    return (
      <>
        <Notice tone="bad">{spaces.failed}</Notice>
        <p className="muted">
          {withSaved
            ? "Jira refused the saved token. Replace the token to list with a new one; the settings saved are kept."
            : "Go back to correct the site address, the email or the token. What you entered is kept."}
        </p>
        <Actions>
          {again("spaces")}
          {back}
          {cancel}
        </Actions>
      </>
    );
  }
  if (spaces.items.length === 0 && !spaces.more) {
    return (
      <>
        <Notice tone="bad">
          {`This account can see no spaces on ${siteOf(fields.baseUrl)}. ${withSaved ? "Replace the token with one for" : "Sign in with"} an account that works in the space, or ask a Jira admin to add this one to it.`}
        </Notice>
        <Actions>
          {again("spaces")}
          {back}
          {cancel}
        </Actions>
      </>
    );
  }

  const key = state.listFor;
  let workType: ReactNode = null;
  if (key === "") workType = null;
  else if (workTypes === "idle" || workTypes === "loading") workType = <p role="status">Reading the work types {key} has…</p>;
  else if ("failed" in workTypes)
    workType = (
      <>
        <Notice tone="bad">{workTypes.failed}</Notice>
        <Actions>{again("work-types")}</Actions>
      </>
    );
  else if (workTypes.items.length === 0 && !workTypes.more)
    workType = (
      <>
        <Notice tone="bad">
          {key} has no work types this account can create. Choose another space, or ask a Jira admin for permission to create work items
          in {key}.
        </Notice>
        <Actions>{again("work-types")}</Actions>
      </>
    );
  else if (state.typing.workType)
    workType = (
      <>
        <JiraWorkTypeField fields={fields} onType={onType} />
        <button type="button" className="btn btn--small" onClick={() => dispatch({ type: "typing-instead", what: "work-type", on: false })}>
          Choose from the list instead
        </button>
      </>
    );
  else
    workType = (
      <>
        <JiraChoice
          id="jira-issue-type"
          label="Work type"
          placeholder="Choose a work type"
          hint={workTypes.more ? unread(workTypes, key, "work types", "If yours is not here, type it instead.") : undefined}
          options={workTypes.items.map((type) => ({ value: type.name, text: type.name }))}
          value={fields.issueType}
          onChoose={(value) => onType("issueType", value)}
        />
        {workTypes.more ? (
          <button type="button" className="btn btn--small" onClick={() => dispatch({ type: "typing-instead", what: "work-type", on: true })}>
            Type the work type instead
          </button>
        ) : null}
      </>
    );

  return (
    <form onSubmit={onward} className="stack stack--tight" aria-label={stepLabel}>
      <div className="settings-fields">
        {state.typing.space ? (
          <>
            <JiraSpaceKeyField fields={fields} onType={onType} onLeave={(typed) => dispatch({ type: "space-chosen", key: typed })} />
            <button type="button" className="btn btn--small" onClick={() => dispatch({ type: "typing-instead", what: "space", on: false })}>
              Choose from the list instead
            </button>
          </>
        ) : (
          <>
            <JiraChoice
              id="jira-project-key"
              label="Space key"
              placeholder="Choose a space"
              hint={spaces.more ? unread(spaces, "Jira", "spaces", "If yours is not here, type its key instead.") : undefined}
              options={spaces.items.map((space) => ({ value: space.key, text: space.name === space.key ? space.key : `${space.key} — ${space.name}` }))}
              value={fields.projectKey}
              onChoose={(value) => dispatch({ type: "space-chosen", key: value })}
            />
            {spaces.more ? (
              <button type="button" className="btn btn--small" onClick={() => dispatch({ type: "typing-instead", what: "space", on: true })}>
                Type the space key instead
              </button>
            ) : null}
          </>
        )}
        {workType}
        <JiraLabelsField fields={fields} onType={onType} />
      </div>
      <Actions>
        <button type="submit" className="btn btn--primary">
          Continue
        </button>
        {back}
        {cancel}
      </Actions>
    </form>
  );
}

export function JiraFlow({
  start,
  editing,
  onCreate,
  onUpdate,
  onSendTest,
  onList,
  onEdit,
  onClose,
}: {
  /** The state the flow opens in. Only a test passes one, to render a step it cannot click to. */
  start?: JiraFlowState;
  /**
   * The saved connection this flow edits (#817), as the list holds it now: the flow opens
   * at its choose step, and what it says of the connection's health is kept current.
   */
  editing?: JiraRow;
  /** Save the connection. The row it answers with is what a test work item is sent from. */
  onCreate: (body: NewIntegration) => Promise<IntegrationRow | void>;
  /** Save an edit: only what changed (#580). Editing without it, nothing can be saved. */
  onUpdate?: (patch: JiraPatch) => Promise<unknown>;
  /** Send one test work item from a saved connection (#614). Without it the box is not offered. */
  onSendTest?: (id: string) => Promise<unknown>;
  /**
   * Read Jira's lists for the choose step (#600). Without it the step asks for the space
   * key and the work type typed.
   */
  onList?: JiraLister;
  /** Open the edit of a connection the workspace already has (#653, #580, #817). */
  onEdit?: (id: string) => void;
  /** Leave the flow. Before the confirm step's save, nothing has been sent anywhere. */
  onClose: () => void;
}) {
  const [state, dispatch] = useReducer(jiraFlowReducer, start ?? (editing === undefined ? openingJiraFlow : editingJiraFlow(editing)));
  const inFlight = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const { step, fields } = state;
  const edit = editSubmission(state);

  // Each step replaces the last one's controls, so the step's own heading takes focus:
  // otherwise a keyboard or screen-reader user is left on a button that is gone.
  useEffect(() => {
    heading.current?.focus();
  }, [step]);

  // The choose step's two reads. Each is numbered, and the reducer takes an answer only
  // to the number it is waiting on: a list that comes back after the credential or the
  // space has changed is of something else.
  const requests = useRef(0);
  const asking = listingAsked(state);
  const wantSpaces = onList !== undefined && asking !== null && state.spaces === "idle";
  const wantWorkTypes =
    onList !== undefined && asking !== null && !wantSpaces && state.workTypes === "idle" && state.listFor !== "" && typeof state.spaces === "object" && "items" in state.spaces;
  useEffect(() => {
    if (onList === undefined || asking === null || !(wantSpaces || wantWorkTypes)) return;
    const id = (requests.current += 1);
    const what: Listable = wantSpaces ? "spaces" : "work-types";
    const key = state.listFor;
    dispatch({ type: "listing", what, id });
    const failed = (refusal: unknown) => dispatch({ type: "listing-failed", what, id, message: messageOf(refusal) });
    if (wantSpaces) onList.spaces(asking).then((answer) => dispatch({ type: "spaces-listed", id, answer }), failed);
    else onList.workTypes({ ...asking, project_key: key }).then((answer) => dispatch({ type: "work-types-listed", id, key, answer }), failed);
    // `asking` is read at the moment a list is wanted, and a change to it makes one wanted again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantSpaces, wantWorkTypes]);

  const onType = (field: keyof JiraFields, value: string) => dispatch({ type: "typed", field, value });
  const onward = (event: FormEvent) => {
    event.preventDefault();
    dispatch({ type: "continue" });
  };

  const connect = async () => {
    const body = submission(state);
    const changed = edit === null || edit.changes.length === 0 || onUpdate === undefined ? null : edit;
    if ((body === null && changed === null) || inFlight.current) return;
    inFlight.current = true;
    dispatch({ type: "saving" });
    try {
      if (changed !== null) {
        await onUpdate?.(changed.patch);
        dispatch({ type: "saved", changed: changed.changes });
      } else if (body !== null) {
        dispatch({ type: "saved", test: await connectThenTest(body, state.sendTest, onCreate, onSendTest) });
      }
    } catch (refusal) {
      const existing = alreadyConnected(refusal);
      dispatch(existing === null ? { type: "refused", message: messageOf(refusal) } : { type: "already-connected", existing });
    } finally {
      inFlight.current = false;
    }
  };

  const withSaved = state.editing !== null && !state.replacing;
  const back = (
    <button type="button" className="btn" disabled={state.saving} onClick={() => dispatch({ type: "back" })}>
      {state.editing !== null && step === "sign-in" ? "Keep the saved token" : "Back"}
    </button>
  );
  // Editing opens at the choose step: before it is only the sign-in, redone to replace the token.
  const backFromWhere = withSaved ? (
    <button type="button" className="btn" onClick={() => dispatch({ type: "replace-token" })}>
      Replace the token
    </button>
  ) : (
    back
  );
  const cancel = (
    <button
      type="button"
      className="btn"
      disabled={state.saving}
      onClick={() => {
        dispatch({ type: "cancelled" });
        onClose();
      }}
    >
      Cancel
    </button>
  );
  const steps = state.editing === null ? JIRA_STEPS : JIRA_EDIT_STEPS;
  const stepLabel = steps.find((each) => each.id === step)?.label;
  const title = (
    <h4 ref={heading} tabIndex={-1} className="step-flow__title">
      {stepLabel}
    </h4>
  );

  let body: ReactNode;
  if (step === "needs") {
    body = (
      <>
        <p className="muted">Investigations open one work item per incident in this space. Have these to hand:</p>
        <ul className="step-flow__needs">
          <li>
            <strong>A Jira Cloud site</strong> — the address you sign in to Jira at, ending in .atlassian.net.
          </li>
          <li>
            <strong>A space to send work items to</strong>, and a work type that space has, such as Task.
          </li>
          <li>
            <strong>An Atlassian API token</strong> for an account that can create work items there. {WHERE_A_TOKEN_IS_MADE}{" "}
            This connection cannot use a token with scopes.
          </li>
        </ul>
        <Actions>
          <button type="button" className="btn btn--primary" onClick={() => dispatch({ type: "continue" })}>
            Continue
          </button>
          {cancel}
        </Actions>
      </>
    );
  } else if (step === "where" && onList !== undefined) {
    body = <ChooseFromLists state={state} dispatch={dispatch} onType={onType} onward={onward} stepLabel={stepLabel} back={backFromWhere} cancel={cancel} />;
  } else if (step === "sign-in" || step === "where") {
    body = (
      <form onSubmit={onward} className="stack stack--tight" aria-label={stepLabel}>
        <div className="settings-fields">
          {step === "sign-in" ? (
            <>
              <JiraSiteField
                fields={fields}
                onType={onType}
                siteError={state.siteError}
                dropped={state.dropped}
                onLeave={() => dispatch({ type: "site-left" })}
              />
              <JiraCredentialFields fields={fields} onType={onType} />
            </>
          ) : (
            <JiraDestinationFields fields={fields} onType={onType} />
          )}
        </div>
        <Actions>
          <button type="submit" className="btn btn--primary">
            Continue
          </button>
          {step === "where" ? backFromWhere : back}
          {cancel}
        </Actions>
      </form>
    );
  } else if (step === "confirm") {
    const summary = jiraSummary(jiraIntegration(fields).config);
    const unchanged = edit !== null && edit.changes.length === 0;
    body = (
      <>
        {state.failure === null ? null : <Notice tone="bad">{state.failure} Your entries are kept.</Notice>}
        {state.sameSpace === null ? null : (
          <div className="stack stack--tight" data-testid="flow-jira-same-space">
            <Notice tone="bad">{sameSpaceInWords(state.sameSpace)}</Notice>
            {onEdit === undefined ? null : (
              <Actions>
                <button type="button" className="btn btn--primary" onClick={() => onEdit(state.sameSpace?.id ?? "")}>
                  Edit that connection instead
                </button>
              </Actions>
            )}
          </div>
        )}
        <p data-testid="flow-jira-summary">
          Work items go to <strong>{summary.space}</strong> as <strong>{summary.workType}</strong> on{" "}
          <strong>{summary.site}</strong>
          {summary.labels === null ? "" : `, labelled ${summary.labels}`}.
        </p>
        {withSaved ? (
          <p className="muted">The saved token is kept.</p>
        ) : (
          <p className="muted">
            Signed in as {fields.email.trim()}. The token is stored encrypted when you {state.editing === null ? "connect" : "save"}, and is
            never shown again.
          </p>
        )}
        {edit === null ? null : unchanged ? (
          <p className="muted" data-testid="flow-jira-changes">
            {NOTHING_CHANGED}
          </p>
        ) : (
          <p data-testid="flow-jira-changes">Saving: {edit.changes.map((change) => `${change}.`).join(" ")}</p>
        )}
        {onSendTest === undefined || state.editing !== null ? null : (
          <div className="step-flow__option">
            <label htmlFor="flow-jira-send-test">
              <input
                type="checkbox"
                id="flow-jira-send-test"
                checked={state.sendTest}
                disabled={state.saving}
                onChange={(event) => dispatch({ type: "test-toggled", on: event.target.checked })}
              />{" "}
              Send a test work item
            </label>
            <p className="muted">
              This creates a real work item in {summary.space}, marked [Test], that someone then has to close.
            </p>
          </div>
        )}
        <Actions>
          <button
            type="button"
            className={state.sameSpace === null ? "btn btn--primary" : "btn"}
            disabled={state.saving || unchanged}
            aria-busy={state.saving}
            onClick={() => void connect()}
          >
            {state.editing === null
              ? state.saving
                ? "Connecting…"
                : state.sameSpace === null
                  ? "Connect Jira"
                  : "Connect a second one anyway"
              : state.saving
                ? "Saving…"
                : state.sameSpace === null
                  ? "Save changes"
                  : "Save it anyway"}
          </button>
          {back}
          {cancel}
        </Actions>
      </>
    );
  } else {
    body = (
      <>
        {state.changed === null ? <Notice tone="good">Jira connected. The token is stored encrypted.</Notice> : <SavedChanges changes={state.changed} />}
        {state.test === "not-asked" ? null : state.test === "asked" ? (
          <p data-testid="flow-jira-test-asked">
            A test work item is on its way to {fields.projectKey.trim()}. Its result shows under Health in the list above; close the work
            item in Jira once you have seen it.
          </p>
        ) : (
          <Notice tone="bad">The test work item was not sent: {state.test.failed}</Notice>
        )}
        <p className="muted">It is in the list above, with its health. Edit it there if Jira starts refusing it.</p>
        <Actions>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </Actions>
      </>
    );
  }

  const flowTitle = state.editing === null ? "Connect Jira Cloud" : "Edit Jira Cloud";
  return (
    <StepFlow title={flowTitle} testId={state.editing === null ? "flow-jira" : "flow-jira-edit"} steps={steps} current={step}>
      {editing === undefined ? null : <EditingLine row={editing} />}
      {title}
      {body}
    </StepFlow>
  );
}
